import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleMap, MarkerF, CircleF, InfoWindowF } from '@react-google-maps/api';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getGeohashPrefixesForViewport, generateGeohash } from '../utils/geohashUtils';
import { clusterRestrooms, ClusterCache } from '../utils/clusterUtils';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { APP_VERSION } from '../config/version';
import logger from '../utils/logger';
import analytics from '../utils/analytics';
import toast from 'react-hot-toast';
import { FaSearch, FaShareSquare, FaSlideshare, FaBell, FaWrench } from "react-icons/fa"; // Removed FaEnvelope
import { FaTowerObservation } from "react-icons/fa6";
import { LuToilet } from "react-icons/lu";
import { IoShareSocialOutline } from "react-icons/io5";
import { MdOutlineManageAccounts, MdLogout, MdPersonOutline, MdMyLocation } from "react-icons/md";
import GamificationCompletionPopup from './GamificationCompletionPopup';
import CelebrationEffect from './CelebrationEffect';
import './MapScreen.css';

// Lazy load modal components to reduce initial bundle size
const AddRestroomModal = lazy(() => import('./AddRestroomModal'));
const RestroomDetailsModal = lazy(() => import('./RestroomDetailsModal'));
const SearchModal = lazy(() => import('./SearchModal'));
const SettingsModal = lazy(() => import('./SettingsModal'));
const UserMenu = lazy(() => import('./UserMenu'));
const InboxPopup = lazy(() => import('./InboxPopup'));
const OnboardingTutorial = lazy(() => import('./OnboardingTutorial'));
const TermsOfServiceModal = lazy(() => import('./TermsOfServiceModal'));
const ContactUsModal = lazy(() => import('./ContactUsModal')); // New import

function MapScreen({ sharedRestroomId }) {
  // Google Maps is now loaded by MapScreenWrapper component

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, signOut, acceptTOS, refreshUser } = useAuth();
  const { 
    restrooms, 
    getNearbyRestrooms, 
    calculateDistance, 
    setRestrooms, 
    fetchRestrooms, 
    getRestroomById, 
    updateRestroom, 
    mergeRestrooms,
    fetchUserRestrooms,
    fetchUserReviews
  } = useData();
  const { formatDistance, getDistanceUnit } = useSettings();
  
  const lastLocationToastTime = useRef(null);
  const LOCATION_TOAST_COOLDOWN = 5000;
  const [suppressLocationToast, setSuppressLocationToast] = useState(false);
  
  const isMobile = /Android|webOS|iPhone|iPad|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768 || /Mobile/.test(navigator.userAgent);
  const buildTime = new Date().toLocaleString();
  
  const [userLocation, setUserLocation] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTOSModal, setShowTOSModal] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [globalLoadingText, setGlobalLoadingText] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isMovingRestroomMode, setIsMovingRestroomMode] = useState(false);
  const [restroomToMove, setRestroomToMove] = useState(null);
  const [selectedRestroom, setSelectedRestroom] = useState(null);
  const [searchFilteredRestrooms, setSearchFilteredRestrooms] = useState(null);
  const [mapZoom, setMapZoom] = useState(14);
  const [displayZoom, setDisplayZoom] = useState(14);
  const zoomUpdateTimeoutRef = useRef(null);
  const displayZoomUpdateTimeoutRef = useRef(null);
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 });
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [mapSelectionMode, setMapSelectionMode] = useState(false);
  const [isAddingRestroomMode, setIsAddingRestroomMode] = useState(false);
  const [selectedMapLocation, setSelectedMapLocation] = useState(null);
  const [showGlobalLoading, setShowGlobalLoading] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [areyouatPlaces, setAreyouatPlaces] = useState([]);
  const [showReviewOnOpen, setShowReviewOnOpen] = useState(false);
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [restroomToMerge, setRestroomToMerge] = useState(null); // Corrected setter name
  const [showMergeConfirmation, setShowMergeConfirmation] = useState(false);
  const [mergeTargetRestroom, setMergeTargetRestroom] = useState(null);
  const [showContactUsModal, setShowContactUsModal] = useState(false); // New state

  // New state for AddRestroomModal data
  const [addModalNearbyRestrooms, setAddModalNearbyRestrooms] = useState([]);
  const [addModalAreYouAtPlaces, setAddModalAreYouAtPlaces] = useState([]);
  const [addModalCheckingNearby, setAddModalCheckingNearby] = useState(false);
  const [gamificationPayload, setGamificationPayload] = useState(null);
  const [celebrationRestroom, setCelebrationRestroom] = useState(null);
  // Rating of the review just given, so the confetti uses the poop icon the
  // user actually rated (null = fall back to the restroom's average, i.e. the
  // add-restroom flow).
  const [celebrationRating, setCelebrationRating] = useState(null);
  // Set when a review is successfully added inside the details modal; the
  // celebration fires when that modal closes (same as the add-restroom flow).
  const reviewJustAddedRef = useRef(false);


  const mapRef = useRef(null);
  const userLocationMarkerRef = useRef(null);
  const mapInitialCenterSet = useRef(false); // New ref to track if map has been initially centered
  const clusterCache = useRef(new ClusterCache(20)); // Cache for clustered results
  const initialNearbyCache = useRef([]); // Cached nearby restrooms fetched on entering add mode
  const initialAreYouAtCache = useRef([]); // Cached Google places fetched on entering add mode
  const initialAreYouAtCacheLocation = useRef(null); // Location the AreYouAt cache was fetched from
  const initialAreYouAtCacheLoaded = useRef(false); // Whether the initial AreYouAt prefetch succeeded
  const initialAreYouAtFetchPromise = useRef(null); // In-flight initial AreYouAt prefetch

  useEffect(() => {
    if (typeof window !== 'undefined') {
      logger.log(`💩 The Crapper Mapper Version ${APP_VERSION} - Build: ${buildTime}`);
    }
  }, []);

  const getPoopIcon = useCallback((rating) => {
    if (!window.google) return null;
    const averageRating = rating || 3;
    let iconNumber;
    if (averageRating <= 1.9) iconNumber = 1;
    else if (averageRating <= 2.9) iconNumber = 2;
    else if (averageRating <= 3.9) iconNumber = 3;
    else if (averageRating <= 4.79) iconNumber = 4;
    else iconNumber = 5;
    
    return {
      url: `${process.env.PUBLIC_URL}/assets/ratings/TCM-RatingDisplay-${iconNumber}.png`,
      scaledSize: new window.google.maps.Size(35, 35),
      anchor: new window.google.maps.Point(17, 17),
    };
  }, []);

  const getUserLocationIcon = useCallback(() => {
    if (!window.google) return null;
    return {
      url: `${process.env.PUBLIC_URL}/thecrappermappergis-logos--01.png`,
      scaledSize: new window.google.maps.Size(56, 56),
      anchor: new window.google.maps.Point(28, 28),
    };
  }, []);

  const getClusterIcon = useCallback((count) => {
    if (!window.google) return null;
    
    // Increased scale to ensure clusters are larger and more visible, 
    // and to naturally overlap less when grid-based clustering is used.
    const baseScale = 22;
    const additionalScale = Math.min(Math.floor(count / 5), 15);
    
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: '#5752e8',
      fillOpacity: 0.7,
      strokeColor: '#FFFFFF',
      strokeOpacity: 1.0,
      strokeWeight: 2,
      scale: baseScale + additionalScale,
    };
  }, []);


  const clusteredRestrooms = useMemo(() => {
    const restroomsToDisplay = searchFilteredRestrooms || restrooms;
    
    // Get map bounds for viewport-based filtering and cache key
    let bounds = null;
    if (mapRef.current) {
      const mapBounds = mapRef.current.getBounds();
      if (mapBounds) {
        bounds = {
          north: mapBounds.getNorthEast().lat(),
          south: mapBounds.getSouthWest().lat(),
          east: mapBounds.getNorthEast().lng(),
          west: mapBounds.getSouthWest().lng()
        };
      }
    }
    
    // Try to get from cache first (including bounds in key)
    const cached = clusterCache.current.get(restroomsToDisplay, displayZoom, isMergeMode, bounds);
    if (cached) {
      return cached;
    }

    // Use optimized clustering function
    const clustered = clusterRestrooms(restroomsToDisplay, displayZoom, isMergeMode, bounds);
    
    // Cache the result (including bounds in key)
    clusterCache.current.set(restroomsToDisplay, displayZoom, isMergeMode, clustered, bounds);
    
    return clustered;
  }, [restrooms, searchFilteredRestrooms, displayZoom, isMergeMode, mapCenter, restrooms.length]);

  // Consolidated useEffect for initial map centering
  useEffect(() => {
    if (mapInitialCenterSet.current) return;

    const setInitialMapPosition = async () => {
      let centerToSet = null;
      let zoomToSet = 14; // Default zoom

      // 1. Check for a preserved map center from a restroom detail flow
      if (location.state?.preservedMapCenter?.lat && location.state?.preservedMapCenter?.lng) {
        centerToSet = location.state.preservedMapCenter;
        zoomToSet = location.state.preservedMapZoom || 17;
        logger.log('[MapInit] Restored map center from preserved restroom selection state.');
      }

      // 2. Check for deep-linked restroom or direct selectedRestroom state
      const selectedRestroomState = location.state?.selectedRestroom;
      let idToProcess = sharedRestroomId || location.state?.selectedRestroomId;
      if (selectedRestroomState?.id) {
        logger.log(`[MapInit] Attempting to center on selectedRestroom from route state: ${selectedRestroomState.id}`);
        const targetRestroom = selectedRestroomState;
        if (targetRestroom.latitude && targetRestroom.longitude) {
          centerToSet = { lat: targetRestroom.latitude, lng: targetRestroom.longitude };
          zoomToSet = location.state?.zoom || 17;
          setSelectedRestroom(targetRestroom);
          setShowDetailsModal(true);
          logger.log(`[MapInit] Centered on selectedRestroom ${targetRestroom.id} from route state.`);
        } else {
          logger.warn(`[MapInit] selectedRestroom state has no coordinates: ${targetRestroom.id}`);
        }
      } else if (idToProcess) {
        logger.log(`[MapInit] Attempting to center on deep-linked restroom: ${idToProcess}`);
        try {
          const targetRestroom = restrooms.find(r => r.id === idToProcess) || await getRestroomById(idToProcess);
          if (targetRestroom && targetRestroom.latitude && targetRestroom.longitude) {
            centerToSet = { lat: targetRestroom.latitude, lng: targetRestroom.longitude };
            zoomToSet = location.state?.zoom || 17; // Zoom in for specific restroom
            setSelectedRestroom(targetRestroom);
            setShowDetailsModal(true);
            logger.log(`[MapInit] Centered on deep-linked restroom ${idToProcess}.`);
          } else {
            logger.warn(`[MapInit] Deep-linked restroom ${idToProcess} not found.`);
          }
        } catch (error) {
          logger.error(`[MapInit] Error fetching deep-linked restroom ${idToProcess}:`, error);
        }
      }

      // 3. If not preserved or deep-linked, check for saved user location
      if (!centerToSet) {
        const saved = sessionStorage.getItem('crappermapper_last_user_location');
        if (saved) {
          try {
            const loc = JSON.parse(saved);
            if (loc.lat && loc.lng) {
              centerToSet = loc;
              zoomToSet = 13; // General user location zoom
              logger.log('[MapInit] Centered on saved user location.');
            }
          } catch (e) {
            console.error('Error parsing saved location:', e);
          }
        }
      }

      // 4. If still no center, try to get current user location
      if (!centerToSet && navigator.geolocation) {
        logger.log('[MapInit] Attempting to fetch current user location.');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
            // Batch state updates together
            setUserLocation(loc);
            setMapCenter(loc);
            setMapZoom(13);
            setInitialFetchDone(false);
            sessionStorage.setItem('crappermapper_last_user_location', JSON.stringify(loc));
            mapInitialCenterSet.current = true;
            logger.log('[MapInit] Centered on newly fetched user location.');
          },
          (error) => logger.log('Location access denied or unavailable:', error.message),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
        );
        return; // Exit, as async geolocation will handle setting center
      }

      // 5. Fallback to default center
      if (!centerToSet) {
        centerToSet = { lat: 39.8283, lng: -98.5795 }; // Default US center
        logger.log('[MapInit] Centered on default location.');
      }
      
      // Batch state updates together
      setMapCenter(centerToSet);
      setMapZoom(zoomToSet);
      setInitialFetchDone(false);
      mapInitialCenterSet.current = true;
    };

    setInitialMapPosition();
  }, [sharedRestroomId, location.state?.selectedRestroomId, restrooms, getRestroomById, navigate, location.pathname]); // Dependencies for this consolidated effect


  // Effect to fetch restrooms when map center or zoom changes after initial load
  useEffect(() => {
    if (mapRef.current && !initialFetchDone) {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const zoom = mapRef.current.getZoom();
        const prefixes = getGeohashPrefixesForViewport(bounds, zoom);
        // U4 debug affordance: surface degenerate viewports (e.g. blocked
        // map tiles yielding no bounds) instead of silently skipping.
        logger.log(`🗺️ Map fetch check: computed ${prefixes.length} geohash prefix(es)`, { zoom, mapCenter });
        if (prefixes.length > 0) {
          logger.log('🗺️ Map idle, checking for restrooms in prefixes:', mapCenter, 'User:', currentUser?.id);
          fetchRestrooms({ geohashPrefixes: prefixes });
          setInitialFetchDone(true);
        } else {
          logger.warn('🗺️ Map fetch skipped: empty geohash prefix set (degenerate viewport?)', { zoom, mapCenter });
        }
      }
    }
  }, [initialFetchDone, fetchRestrooms, mapCenter, currentUser]);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      logger.log('👤 User profile loaded/changed, resetting initial fetch to pick up user-specific restrooms');
      setInitialFetchDone(false);
    }
  }, [currentUser?.id]);

  const fetchUserLocation = useCallback(() => { // Removed shouldCenter param, as centering is now handled by useEffect
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setUserLocation(loc);
          sessionStorage.setItem('crappermapper_last_user_location', JSON.stringify(loc));
        },
        (error) => logger.log('Location access denied or unavailable:', error.message),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem('crappermapper_last_user_location');
    if (saved && !userLocation) {
      try {
        const loc = JSON.parse(saved);
        setUserLocation(loc);
        logger.log('📍 Restored user location from session storage');
      } catch (e) {
        console.error('Error parsing saved location:', e);
      }
    }
  }, []);

  // Fetch user location on mount, but don't auto-center unless no other center is set
  useEffect(() => {
    if (!userLocation && !mapInitialCenterSet.current) {
      fetchUserLocation();
    }
  }, [userLocation, fetchUserLocation]);


  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!currentUser) return;
      try {
        const functions = getFunctions();
        const getUnreadNotificationsCount = httpsCallable(functions, 'GetUnreadNotificationsCount');
        const result = await getUnreadNotificationsCount({
          database_id_hash: localStorage.getItem('database_id_hash') || ''
        });
        if (result.data.success) setUnreadCount(result.data.count);
      } catch (error) {
        console.error("Error checking unread notifications:", error);
      }
    };
    fetchUnreadCount();
  }, [currentUser]);

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) return;
    const tutorialCompleted = localStorage.getItem(`crapperMapper_tutorialCompleted_${uid}`);
    if (tutorialCompleted) return;
    const timer = setTimeout(() => setShowOnboarding(true), 1000);
    return () => clearTimeout(timer);
  }, [currentUser?.uid]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    window.mapInstance = {
      setView: (center, zoom) => {
        map.setCenter({ lat: center[0], lng: center[1] });
        map.setZoom(zoom);
      },
      closePopup: () => setActiveMarkerId(null)
    };
  }, []);

  const isLocationWithinRadius = useCallback(() => {
    if (currentUser?.isAdmin) return true;
    if (!userLocation || !selectedMapLocation || !mapRef.current) return false;

    const currentMapCenter = { lat: mapRef.current.getCenter().lat(), lng: mapRef.current.getCenter().lng() };
    const MAX_DISTANCE_METERS = 50;
    const distanceInMeters = calculateDistance(selectedMapLocation.lat, selectedMapLocation.lng, currentMapCenter.lat, currentMapCenter.lng) * 1609.34;
    return distanceInMeters <= MAX_DISTANCE_METERS;
  }, [userLocation, selectedMapLocation, calculateDistance, currentUser?.isAdmin]);

  const handleMapClick = (e) => {
    if (isAddingRestroomMode) {
      return;
    }
    if (mapSelectionMode) {
      const latlng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setSelectedMapLocation(latlng);
      
      const fetchAreyouatPlacesForFineTuned = async () => {
        try {
          const functions = getFunctions();
          const areYouAtFn = httpsCallable(functions, 'AreYouAt');
          // Use userLocation (the middle of the blue circle) instead of latlng (fine-tuned map location)
          const result = await areYouAtFn({ latitude: userLocation.lat, longitude: userLocation.lng });
          if (result.data && result.data.success && result.data.places) {
            setAreyouatPlaces(result.data.places);
          }
        } catch (error) {
          console.error('Error fetching AreYouAt places for fine-tuned location:', error);
        }
      };
      fetchAreyouatPlacesForFineTuned();

      setMapSelectionMode(false);
      setShowAddModal(true);
    } else {
      setActiveMarkerId(null);
    }
  };

  const handleMapRightClick = (e) => {
    if (currentUser?.isAdmin) {
      const latlng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setSelectedMapLocation(latlng);
      
      const fetchAreyouatPlacesForAdmin = async () => {
        try {
          const functions = getFunctions();
          const areYouAtFn = httpsCallable(functions, 'AreYouAt');
          const result = await areYouAtFn({ latitude: latlng.lat, longitude: latlng.lng });
          if (result.data && result.data.success && result.data.places) {
            setAreyouatPlaces(result.data.places);
            setShowAddModal(true); // Open modal ONLY after places are fetched to ensure consistency
            toast.success('Admin: Adding restroom at clicked location', { icon: '🔧' });
          } else {
            setAreyouatPlaces([]);
            setShowAddModal(true);
            toast.success('Admin: Adding restroom at clicked location', { icon: '🔧' });
          }
        } catch (error) {
          console.error('Error fetching AreYouAt places for admin long-press:', error);
          setAreyouatPlaces([]);
          setShowAddModal(true);
          toast.success('Admin: Adding restroom at clicked location', { icon: '🔧' });
        }
      };
      fetchAreyouatPlacesForAdmin();
    }
  };

  const mergePlaces = (existing, incoming) => {
    const seen = new Set();
    const merged = [];
    [...existing, ...incoming].forEach(place => {
      if (!place.placeId || !seen.has(place.placeId)) {
        if (place.placeId) seen.add(place.placeId);
        merged.push(place);
      }
    });
    return merged;
  };

  const handleConfirmAddRestroom = async () => {
    if (!isLocationWithinRadius()) {
      toast.error('Your current map selection is outside the allowed radius from your initial location.');
      return;
    }
    const confirmedLocation = { lat: mapRef.current.getCenter().lat(), lng: mapRef.current.getCenter().lng() };
    setSelectedMapLocation(confirmedLocation);

    setAddModalCheckingNearby(true);
    setAddModalNearbyRestrooms([]);

    try {
      // Wait for the initial AreYouAt prefetch if it is still in flight
      if (initialAreYouAtFetchPromise.current) {
        try { await initialAreYouAtFetchPromise.current; } catch (e) { /* handled at prefetch site */ }
      }

      // Immediately display the cached Google places
      setAddModalAreYouAtPlaces(initialAreYouAtCache.current);

      // Filter cached nearby restrooms by 50m from confirmed location
      const nearbyWithin50m = initialNearbyCache.current.filter(r => {
        if (!r.latitude || !r.longitude) return false;
        const distanceMiles = calculateDistance(confirmedLocation.lat, confirmedLocation.lng, r.latitude, r.longitude);
        return distanceMiles * 1609.34 <= 50;
      });
      setAddModalNearbyRestrooms(nearbyWithin50m);
    } catch (error) {
      console.error('Error loading cached data for AddRestroomModal:', error);
    } finally {
      setAddModalCheckingNearby(false);
      setIsAddingRestroomMode(false);
      setShowAddModal(true);
    }

    // If the user moved more than 5m from the initial prefetch location (or the
    // initial prefetch failed), refresh Google places from the confirmed location
    // in the background and merge the results into the list that is already shown.
    const cacheLocation = initialAreYouAtCacheLocation.current;
    const movedBeyondThreshold = cacheLocation &&
      calculateDistance(confirmedLocation.lat, confirmedLocation.lng, cacheLocation.lat, cacheLocation.lng) * 1609.34 > 5;

    if (!initialAreYouAtCacheLoaded.current || movedBeyondThreshold) {
      try {
        const functions = getFunctions();
        const areYouAtFn = httpsCallable(functions, 'AreYouAt');
        const areYouAtResult = await areYouAtFn({
          latitude: confirmedLocation.lat,
          longitude: confirmedLocation.lng
        });
        if (areYouAtResult.data && areYouAtResult.data.success && Array.isArray(areYouAtResult.data.places)) {
          console.log(`[AddRestroom] AreYouAt refresh returned ${areYouAtResult.data.places.length} places from confirmed location`);
          setAddModalAreYouAtPlaces(prev => mergePlaces(prev, areYouAtResult.data.places));
        }
      } catch (error) {
        console.error('Error refreshing AreYouAt places for moved location:', error);
      }
    }

    initialNearbyCache.current = [];
    initialAreYouAtCache.current = [];
    initialAreYouAtCacheLocation.current = null;
    initialAreYouAtCacheLoaded.current = false;
    initialAreYouAtFetchPromise.current = null;
  };

  const handleCancelAddRestroom = () => {
    setIsAddingRestroomMode(false);
    setSelectedMapLocation(null);
    setAddModalNearbyRestrooms([]);
    setAddModalAreYouAtPlaces([]);
    setAreyouatPlaces([]);
    initialNearbyCache.current = [];
    initialAreYouAtCache.current = [];
    initialAreYouAtCacheLocation.current = null;
    initialAreYouAtCacheLoaded.current = false;
    initialAreYouAtFetchPromise.current = null;
  };

  const handleMoveRestroom = (restroom) => {
    setRestroomToMove(restroom);
    setIsMovingRestroomMode(true);
    setShowDetailsModal(false);
    toast('Move map to new location', { icon: '📍' });
  };

  const handleConfirmMoveRestroom = async () => {
    if (!restroomToMove || !mapRef.current) return;

    try {
      setShowGlobalLoading(true);
      setGlobalLoadingText('Updating location...');
      
      const center = mapRef.current.getCenter();
      const newLat = center.lat();
      const newLng = center.lng();
      const newGeohash = generateGeohash(newLat, newLng);

      await updateRestroom(restroomToMove.id, {
        latitude: newLat,
        longitude: newLng,
        geohash: newGeohash,
        isVerified: true
      });

      toast.success('Restroom moved successfully! 📍');
      setIsMovingRestroomMode(false);
      setRestroomToMove(null);
      
      fetchRestrooms();
    } catch (error) {
      console.error('Error moving restroom:', error);
      toast.error('Failed to move restroom');
    } finally {
      setShowGlobalLoading(false);
      setGlobalLoadingText('');
    }
  };

  const handleCancelMoveRestroom = () => {
    setIsMovingRestroomMode(false);
    setRestroomToMove(null);
    toast('Move cancelled', { icon: '↩️' });
  };

  const handleBackToLocation = () => {
    setSelectedRestroom(null);
    setShowDetailsModal(false);
    setActiveMarkerId(null);

    if (userLocation) {
      if (mapRef.current) {
        mapRef.current.panTo(userLocation);
        mapRef.current.setZoom(13);
      }
      setMapCenter(userLocation);
      setMapZoom(13);
    } else {
      fetchUserLocationAndCenter();
    }
  };

  const fetchUserLocationAndCenter = () => {
    if (!navigator.geolocation) {
      toast.error('Location is not available on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setUserLocation(loc);
        sessionStorage.setItem('crappermapper_last_user_location', JSON.stringify(loc));
        if (mapRef.current) {
          mapRef.current.panTo(loc);
          mapRef.current.setZoom(13);
        }
        setMapCenter(loc);
        setMapZoom(13);
      },
      (error) => {
        toast.error('Could not get your location. Please check your location permissions.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleAddRestroom = async () => {
    if (!currentUser?.tosAccepted) {
      setShowTOSModal(true);
      return;
    }
    
    if (userLocation) {
      setSelectedMapLocation(userLocation);
      setMapCenter({ lat: userLocation.lat, lng: userLocation.lng });
      setMapZoom(19);

      // Ensure the map moves to the user's current location before entering add mode
      if (mapRef.current) {
        mapRef.current.panTo(userLocation);
      }

      setIsAddingRestroomMode(true);

      // Pre-fetch nearby restrooms within 75m
      try {
        const functions = getFunctions();
        const getNearbyRestroomsFn = httpsCallable(functions, 'GetNearbyRestrooms');
        const result = await getNearbyRestroomsFn({
          latitude: userLocation.lat,
          longitude: userLocation.lng,
          radius: 75
        });
        if (result.data?.success && result.data?.restrooms) {
          initialNearbyCache.current = result.data.restrooms;
        }
      } catch (error) {
        console.error('[AddRestroom] Pre-fetch nearby restrooms failed:', error);
        initialNearbyCache.current = [];
      }

      // Pre-fetch Google places within 50m (the blue circle radius) so the list
      // is ready instantly when the user confirms.
      try {
        const functions = getFunctions();
        const areYouAtFn = httpsCallable(functions, 'AreYouAt');
        const areYouAtResultPromise = areYouAtFn({
          latitude: userLocation.lat,
          longitude: userLocation.lng
        });
        initialAreYouAtFetchPromise.current = areYouAtResultPromise;
        const areYouAtResult = await areYouAtResultPromise;
        if (areYouAtResult.data?.success && Array.isArray(areYouAtResult.data.places)) {
          initialAreYouAtCache.current = areYouAtResult.data.places;
          initialAreYouAtCacheLocation.current = userLocation;
          initialAreYouAtCacheLoaded.current = true;
          console.log(`[AddRestroom] AreYouAt prefetch returned ${areYouAtResult.data.places.length} places from initial location`);
        }
      } catch (error) {
        console.error('[AddRestroom] Pre-fetch AreYouAt places failed:', error);
        initialAreYouAtCache.current = [];
        initialAreYouAtCacheLoaded.current = false;
      }
    } else {
      toast.error('Please wait for your location to be found before adding a restroom.');
      setIsAddingRestroomMode(false);
    }
  };

  useEffect(() => {
    if (isAddingRestroomMode && !userLocation) {
      toast.error('Please wait for your location to be found before adding a restroom.');
      setIsAddingRestroomMode(false);
    }
  }, [isAddingRestroomMode, userLocation]);


  const handleTOSAccept = async () => {
    try {
      await acceptTOS();
      setShowTOSModal(false);
      toast.success('Terms accepted! 🎉');
      
      // If we were trying to add a restroom, continue the flow
      if (isAddingRestroomMode) {
        // No need to call handleAddRestroom() again as isAddingRestroomMode is already true,
        // but we might need to trigger the location fetching if it wasn't done
        if (!userLocation) {
          fetchUserLocation();
        }
      }
    } catch (error) {
      toast.error(`Failed to accept terms. Please try again. ${error.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    
    console.log("[DEBUG_LOG] formatDate received:", timestamp);
    
    try {
      let date;
      
      if (timestamp && typeof timestamp === 'object') {
        if (typeof timestamp.toDate === 'function') {
          date = timestamp.toDate();
        } else if (timestamp.seconds !== undefined) {
          date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds !== undefined) {
          date = new Date(timestamp._seconds * 1000);
        } else {
          date = new Date(timestamp);
        }
      } else {
        date = new Date(timestamp);
      }
      
      if (!date || isNaN(date.getTime())) {
        console.log("[DEBUG_LOG] formatDate produced invalid date for:", timestamp);
        return null;
      }
      
      return date.toLocaleString([], { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (e) {
      console.error("[DEBUG_LOG] Error in formatDate:", e);
      return null;
    }
  };

  const handleRestroomClick = async (restroom) => {
    if (isMergeMode) {
      if (restroom.id === restroomToMerge.id) {
        toast.error("You cannot merge a restroom into itself!");
        return;
      }
      setMergeTargetRestroom(restroom);
      setShowMergeConfirmation(true);
      return;
    }
    setShowReviewOnOpen(false);
    setSelectedRestroom(restroom);
    setShowDetailsModal(true);

    try {
      const fullRestroom = await getRestroomById(restroom.id, true);
      if (fullRestroom) {
        setSelectedRestroom(current => current?.id === restroom.id ? fullRestroom : current);
      }
    } catch (error) {
      logger.error(`Error fetching full restroom details for ${restroom.id}:`, error);
    }
    
    if (restroom && restroom.latitude && restroom.longitude) {
      // Batch map state updates together
      setMapCenter({ lat: restroom.latitude, lng: restroom.longitude });
      setMapZoom(17);
    }

    if (restroom.hasChangingTable) {
      toast.success('This restroom has a changing table!👶', { duration: 2000 });
    }
  };

  const handleStartMerge = (restroom) => {
    // Batch state updates together
    setRestroomToMerge(restroom);
    setIsMergeMode(true);
    setShowDetailsModal(false);
    toast('Select the restroom you want to merge into', { icon: '🔄', duration: 4000 });
  };

  const handleCancelMerge = () => {
    // Batch state updates together
    setIsMergeMode(false);
    setRestroomToMerge(null);
    setMergeTargetRestroom(null);
    setShowMergeConfirmation(false);
    toast('Merge cancelled');
  };

  const handleExecuteMerge = async (keepTarget = true) => {
    const source = keepTarget ? restroomToMerge : mergeTargetRestroom;
    const target = keepTarget ? mergeTargetRestroom : restroomToMove;

    setShowGlobalLoading(true);
    setGlobalLoadingText('Merging restrooms...');
    
    try {
      await mergeRestrooms(source.id, target.id);
      toast.success('Restrooms merged successfully! 🎉');
      // Batch state updates together
      setIsMergeMode(false);
      setRestroomToMerge(null);
      setMergeTargetRestroom(null);
      setShowMergeConfirmation(false);
      setSelectedRestroom(null);
    } catch (error) {
      toast.error('Failed to merge restrooms: ' + error.message);
    } finally {
      setShowGlobalLoading(false);
      setGlobalLoadingText('');
    }
  };

  const handleClusterClick = (cluster) => {
    if (cluster.type === 'cluster') {
      if (mapRef.current && cluster.restrooms && cluster.restrooms.length > 0 && window.google) {
        const bounds = new window.google.maps.LatLngBounds();
        cluster.restrooms.forEach(r => {
          if (r.latitude && r.longitude) {
            bounds.extend({ lat: r.latitude, lng: r.longitude });
          }
        });
        if (!bounds.isEmpty()) {
          mapRef.current.fitBounds(bounds, 80);
        }
      }
    } else {
      handleRestroomClick(cluster);
    }
  };

  const handleInviteFriends = () => {
    setShowHamburgerMenu(false);
    analytics.track('user_invited_friends', {
      has_referral_code: !!currentUser?.referralCode,
      referral_code: currentUser?.referralCode || null,
      invited_at: new Date().toISOString()
    });
    
    let appUrl = 'https://thecrappermapper.com';
    if (currentUser?.referralCode) appUrl = `https://thecrappermapper.com/inappinvite?ref=${currentUser.referralCode}`;
    
    const shareData = {
      title: 'The Crapper Mapper',
      text: 'Discover the #1 app for your #2 needs and start to Doo Better with The Crapper Mapper!',
      url: appUrl
    };

    setTimeout(async () => {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          toast.success('Thanks for sharing! 🎉');
        } catch (err) {
          if (err.name !== 'AbortError') fallbackToCopyLink(shareData, appUrl);
        }
      } else {
        fallbackToCopyLink(shareData, appUrl);
      }
    }, 100);
  };

  const fallbackToCopyLink = async (shareData, appUrl) => {
    try {
      const shareText = `${shareData.text}\n${shareData.url}`;
      await navigator.clipboard.writeText(shareText);
      toast.success('Link copied to clipboard! 📋\nShare it with your friends!', { duration: 3000 });
    } catch (err) {
      toast('Share The Crapper Mapper:\n' + appUrl, { duration: 6000, icon: '📤' });
    }
  };

  const handleClearSearchFilter = () => {
    setSearchFilteredRestrooms(null);
  };

  const handleSelectExistingRestroom = useCallback(async (partialRestroom) => {
    let fullRestroom = restrooms.find(r => r.id === partialRestroom.id);
    if (!fullRestroom) {
      try {
        setGlobalLoadingText('Loading restroom details...');
        setShowGlobalLoading(true);
        fullRestroom = await getRestroomById(partialRestroom.id);
      } catch (error) {
        console.error('Error fetching restroom by ID:', error);
      } finally {
        setShowGlobalLoading(false);
        setGlobalLoadingText('');
      }
    }

    if (fullRestroom) {
      setSelectedRestroom(fullRestroom);
      setShowReviewOnOpen(true);
      setShowDetailsModal(true);
      setShowAddModal(false);
    } else {
      toast.error('Could not find full restroom details.');
      console.error('Error: Full restroom details not found for ID:', partialRestroom.id);
    }
  }, [
    restrooms,
    getRestroomById,
    setShowGlobalLoading,
    setGlobalLoadingText,
    setSelectedRestroom,
    setShowReviewOnOpen,
    setShowDetailsModal,
    setShowAddModal,
    toast
  ]);

  const handleRestroomAdded = useCallback((newRestroom) => {
    setRestrooms(prevRestrooms => {
      if (prevRestrooms.some(r => r.id === newRestroom.id)) return prevRestrooms;
      return [...prevRestrooms, newRestroom];
    });
    fetchUserRestrooms();
    fetchUserReviews();
    refreshUser();
    setCelebrationRestroom(newRestroom);
    setCelebrationRating(null); // New restroom: confetti uses its average rating
  }, [setRestrooms, fetchUserRestrooms, fetchUserReviews, refreshUser]);

  const handleReviewAdded = useCallback((updatedRestroom, reviewRating) => {
    setRestrooms(prevRestrooms =>
      prevRestrooms.map(restroom =>
        restroom.id === updatedRestroom.id ? updatedRestroom : restroom
      )
    );
    setSelectedRestroom(updatedRestroom);
    reviewJustAddedRef.current = true;
    if (typeof reviewRating === 'number') setCelebrationRating(reviewRating);
    fetchUserReviews();
    refreshUser();
  }, [setRestrooms, fetchUserReviews, setSelectedRestroom, refreshUser]);

  const handleMapIdle = useCallback(() => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      const zoom = mapRef.current.getZoom();
      
      setDisplayZoom(zoom);
      
      if (bounds) {
        if (window.fetchTimeout) clearTimeout(window.fetchTimeout);
        window.fetchTimeout = setTimeout(() => {
          const prefixes = getGeohashPrefixesForViewport(bounds, zoom);
          // U4 debug affordance: log prefix count so empty-viewport
          // fetches are diagnosable instead of silent.
          logger.log(`🗺️ Map idle: computed ${prefixes.length} geohash prefix(es)`, { zoom, mapCenter });
          if (prefixes.length > 0) {
            fetchRestrooms({ geohashPrefixes: prefixes });
            setInitialFetchDone(true);
          } else {
            logger.warn('🗺️ Map idle fetch skipped: empty geohash prefix set (degenerate viewport?)', { zoom, mapCenter });
          }
        }, 800); // Increased from 500ms to 800ms for better performance
      }
    }
  }, [fetchRestrooms, mapCenter]);

  return (
    <div className="map-screen">
      {!isMobile && (
        <div className="map-header">
          <div className="header-left">
            <img src="/app-icon.png" alt="The Crapper Mapper" className="header-logo" />
            <h1>The Crapper Mapper</h1>
          </div>
          <div className="header-right">
            <button className="btn btn-secondary search-btn" onClick={() => setShowSearchModal(true)}>
              <FaSearch style={{ color: 'white', marginRight: '8px' }} /> Search
            </button>
            <button className="btn btn-secondary inbox-btn" onClick={() => setShowInbox(true)} style={{ marginLeft: '8px', position: 'relative' }}>
              <FaBell style={{ color: 'white', marginRight: '8px' }} /> Inbox
              {unreadCount > 0 && <span className="header-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
            {searchFilteredRestrooms && (
              <button className="btn btn-secondary" onClick={handleClearSearchFilter} style={{ marginLeft: '8px' }}>
                ✕ Clear Filter ({searchFilteredRestrooms.length})
              </button>
            )}
            {/*<button className="btn btn-secondary" onClick={handleInviteFriends} style={{ marginLeft: '8px' }}>*/}
            {/*  📤 Invite Friends*/}
            {/*</button>*/}
            <button className="user-menu-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
              👤 {currentUser?.username}
            </button>
          </div>
        </div>
      )}
      
      {showGlobalLoading && (
        <div className="global-loading-overlay">
            <div className="global-loading-content">
              <img src="/app-icon.png" alt="The Crapper Mapper" className="loading-logo" />
              <div className="global-loading-text">{globalLoadingText}</div>
          </div>
        </div>
      )}
      
      {isMobile && !showAddModal && !showDetailsModal && !showSearchModal && !showUserMenu && !showInbox && !isAddingRestroomMode && !showContactUsModal && (
        <>
          {userLocation && (
            <button className="back-to-location-btn" onClick={handleBackToLocation} aria-label="Go to my location"><MdMyLocation size={26} color="white" /></button>
          )}
          <div className="mobile-home-bar">
            <button className="home-bar-btn hamburger-btn" onClick={() => setShowHamburgerMenu(!showHamburgerMenu)}>
              <img src={`${process.env.PUBLIC_URL}/TCM-nav-09.png`} alt="Menu" className="nav-icon" />
              {unreadCount > 0 && <div className="mobile-nav-dot"></div>}
            </button>
            <div className="home-bar-separator">|</div>
            <button className="home-bar-btn app-icon-btn" onClick={handleAddRestroom}>
              <img src={`${process.env.PUBLIC_URL}/TCM-nav-10.png`} alt="Add Restroom" className="nav-icon" />
            </button>
            <div className="home-bar-separator">|</div>
            <button className="home-bar-btn daily-flush-btn" onClick={() => navigate('/app/daily-flush')}>
              <img src={`${process.env.PUBLIC_URL}/TCM-nav-11.png`} alt="Daily Flush" className="nav-icon" />
            </button>
          </div>
          
          {showHamburgerMenu && (
            <div className="mobile-hamburger-menu-bottom">
              <button className="menu-item" onClick={() => { setShowSearchModal(true); setShowHamburgerMenu(false); }}>
                <FaSearch style={{ color: '#231F20', marginRight: '8px' }} /> Search
              </button>
              <button className="menu-item" onClick={() => { setShowInbox(true); setShowHamburgerMenu(false); }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FaBell style={{ color: '#231F20', marginRight: '8px' }} /> Inbox
                </div>
                {unreadCount > 0 && <span className="menu-badge">{unreadCount}</span>}
              </button>
              <button className="menu-item" onClick={() => { navigate('/app/daily-flush'); setShowHamburgerMenu(false); }}>
                <LuToilet style={{ color: '#231F20', marginRight: '8px' }} /> The Daily Flush
              </button>
              <button className="menu-item" onClick={() => { navigate('/app/porcelain-pyramid'); setShowHamburgerMenu(false); }}>
                <FaTowerObservation style={{ color: '#231F20', marginRight: '8px' }} /> Porcelain Pyramid
              </button>
              {/*<button className="menu-item" onClick={() => { handleInviteFriends(); setShowHamburgerMenu(false); }}>*/}
              {/*  <FaShareSquare style={{ color: '#231F20', marginRight: '8px' }} /> Invite Friends*/}
              {/*</button>*/}
              <button className="menu-item" onClick={() => { navigate('/socials'); setShowHamburgerMenu(false); }}>
                <IoShareSocialOutline style={{ color: '#231F20', marginRight: '8px' }} /> Social
              </button>
              <button className="menu-item" onClick={() => { setShowUserMenu(true); setShowHamburgerMenu(false); }}>
                <MdOutlineManageAccounts style={{ color: '#231F20', marginRight: '8px' }} /> My Account
              </button>
              {currentUser?.isAdmin && (
                <button className="menu-item" onClick={() => { window.location.href = '/app/admin'; setShowHamburgerMenu(false); }}>
                  <MdOutlineManageAccounts style={{ color: '#231F20', marginRight: '8px' }} /> Admin Dashboard
                </button>
              )}
              {/* New Contact Us button */}
              <button className="menu-item" onClick={() => { setShowContactUsModal(true); setShowHamburgerMenu(false); }}>
                Contact Us
              </button>
              {/*<button className="menu-item" onClick={() => { window.location.href = '/ambassador'; setShowHamburgerMenu(false); }}>*/}
              {/*  <FaSlideshare style={{ color: '#231F20', marginRight: '8px' }} /> Join Ambassador Team*/}
              {/*</button>*/}
              <button className="menu-item logout" onClick={() => { signOut(); setShowHamburgerMenu(false); }}>
                <MdLogout style={{ color: '#231F20', marginRight: '8px' }} /> Logout
              </button>
            </div>
          )}
        </>
      )}

      <div className="map-container">
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          onClick={handleMapClick}
          onRightClick={handleMapRightClick}
          onIdle={handleMapIdle}
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            gestureHandling: 'greedy',
            minZoom: 3,
            maxZoom: 20,
            styles: [
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              { featureType: 'transit', stylers: [{ visibility: 'off' }] }
            ],
            clickableIcons: false,
            keyboardShortcuts: false,
            disableDoubleClickZoom: true
          }}
        >
          {userLocation && !isAddingRestroomMode && (
            <>
              <MarkerF 
                onLoad={(marker) => (userLocationMarkerRef.current = marker)}
                position={userLocation}
                icon={getUserLocationIcon()}
              />

              {mapSelectionMode && (
                <CircleF
                  center={selectedMapLocation || userLocation}
                  radius={50}
                  options={{
                    strokeColor: '#5752e8',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: '#5752e8',
                    fillOpacity: 0.1,
                    clickable: false,
                  }}
                />
              )}
            </>
          )}

          {isAddingRestroomMode && selectedMapLocation && userLocation && ( // Use selectedMapLocation for the circle
            <CircleF
              center={selectedMapLocation}
              radius={50}
              options={{
                strokeColor: '#5752e8',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#5752e8',
                fillOpacity: 0.1,
                clickable: false,
              }}
            />
          )}

          {clusteredRestrooms.map((item) => (
            <MarkerF
              key={item.id}
              position={{ lat: item.latitude, lng: item.longitude }}
              icon={item.type === 'cluster' ? getClusterIcon(item.count) : getPoopIcon(item.averageRating || item.rating)}
              label={item.type === 'cluster' ? { text: String(item.count), color: 'white', fontWeight: 'bold' } : null}
              onClick={() => {
                // While confirming a new restroom's location, clicking an
                // existing restroom (or cluster) must not open its details.
                if (isAddingRestroomMode) return;
                if (item.type === 'cluster') handleClusterClick(item);
                else handleRestroomClick(item);
              }}
              // Performance optimizations for markers
              options={{
                optimized: true,
                zIndex: item.type === 'cluster' ? 100 : 50
              }}
            >
            </MarkerF>
          ))}

        </GoogleMap>
        
        {isAddingRestroomMode && (
          <div 
            className="add-restroom-center-overlay"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 1000
            }}
          >
            <img 
              src={`${process.env.PUBLIC_URL}/thecrappermappergis-logos--01.png`} 
              alt="Restroom location"
              style={{ width: '56px', height: '56px', objectFit: 'contain' }}
            />
          </div>
        )}
        
        {isMovingRestroomMode && (
          <div 
            className="move-restroom-overlay"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              border: '2px solid #5752e8',
              borderRadius: '50%',
              position: 'relative',
              boxShadow: '0 0 10px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '2px',
                height: '40px',
                backgroundColor: '#4a3728'
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '40px',
                height: '2px',
                backgroundColor: '#4a3728'
              }} />
            </div>
          </div>
        )}
      </div>

      {celebrationRestroom && mapRef.current && window.google && (
        <CelebrationEffect
          key={celebrationRestroom.id}
          map={mapRef.current}
          restroom={celebrationRestroom}
          rating={celebrationRating}
          onDone={() => setCelebrationRestroom(null)}
        />
      )}

      {searchFilteredRestrooms && !showAddModal && !showDetailsModal && !showSearchModal && !showUserMenu && !showInbox && !isAddingRestroomMode && !showContactUsModal && (
        <div className="mobile-filter-indicator">
          <div className="filter-info">🔍 Showing {searchFilteredRestrooms.length} results</div>
          <button className="btn btn-clear-filter" onClick={handleClearSearchFilter} style={{ marginLeft: '8px' }}>
                ✕ Clear Filter ({searchFilteredRestrooms.length})
              </button>
        </div>
      )}

      {!showAddModal && !showDetailsModal && !showSearchModal && !showUserMenu && !showInbox && !isAddingRestroomMode && !showContactUsModal && (
        <>
          {userLocation && (
            <button className="back-to-location-btn" onClick={handleBackToLocation} aria-label="Go to my location"><MdMyLocation size={26} color="white" /></button>
          )}
          <button 
            className="add-restroom-btn"
            onClick={handleAddRestroom}
            aria-label="Add Restroom"
          >
            {currentUser?.isAdmin 
              ? <FaWrench size={24} color="white" /> 
              : <img src={`${process.env.PUBLIC_URL}/TCM-nav-10.png`} alt="Add Restroom" className="nav-icon" />}
          </button>
        </>
      )}
      
      {isAddingRestroomMode && (
        <div 
          className="add-restroom-confirmation-dialog"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            zIndex: 1600,
            maxWidth: '300px',
            borderTop: '4px solid #5752e8',
            textAlign: 'center'
          }}
        >
          <p></p>
          <div className="confirmation-actions">
            <button 
              className="btn btn-primary"
              style={{ backgroundColor: '#614F48', color: 'white' }}
              onClick={handleConfirmAddRestroom}
              disabled={!isLocationWithinRadius()}
            >
              ✓ Confirm Location
            </button>
            <button className="btn btn-secondary" onClick={handleCancelAddRestroom}>✗ Cancel</button>
          </div>
          {!currentUser?.isAdmin && !isLocationWithinRadius() && (
            <p style={{ color: 'red', marginTop: '10px' }}>You must be within the blue circle to confirm this location.</p>
          )}
        </div>
      )}

      {showMergeConfirmation && restroomToMove && mergeTargetRestroom && (
        <div 
          className="confirmation-overlay"
          style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            zIndex: 1600,
            maxWidth: '350px',
            width: '90%',
            borderTop: '5px solid #2c3e50',
            textAlign: 'center'
          }}
        >
          <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Merge Restrooms</h3>
          <p style={{ marginBottom: '10px', fontSize: '14px' }}>
            Choose which restroom to <strong>KEEP</strong>. The other's reviews will be moved into it.
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            <button 
              className="btn"
              onClick={() => handleExecuteMerge(true)}
              style={{ 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                border: '2px solid #2c3e50',
                borderRadius: '8px',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              <div style={{ fontWeight: 'bold' }}>Keep: {mergeTargetRestroom.name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Updated: {formatDate(mergeTargetRestroom.updatedAt) || formatDate(mergeTargetRestroom.createdAt) || 'No date available'}
              </div>
            </button>
            
            <button 
              className="btn"
              onClick={() => handleExecuteMerge(false)}
              style={{ 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                border: '2px solid #2c3e50',
                borderRadius: '8px',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              <div style={{ fontWeight: 'bold' }}>Keep: {restroomToMove.name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Updated: {formatDate(restroomToMove.updatedAt) || formatDate(restroomToMove.createdAt) || 'No date available'}
              </div>
            </button>
          </div>

          <button 
            className="btn btn-secondary" 
            onClick={handleCancelMerge}
            style={{ width: '100%' }}
          >
            ✗ Cancel Merge
          </button>
        </div>
      )}

      {isMergeMode && !showMergeConfirmation && (
        <div 
          className="merge-instruction-overlay"
          style={{
            position: 'absolute',
            bottom: '100px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(44, 62, 80, 0.95)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '30px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            zIndex: 1500,
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            whiteSpace: 'nowrap'
          }}
        >
          <span>Select the restroom to merge "{restroomToMove?.name}" with</span>
          <button 
            onClick={handleCancelMerge}
            style={{
              background: 'white',
              color: '#2c3e50',
              border: 'none',
              borderRadius: '15px',
              padding: '4px 12px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {isMovingRestroomMode && (
        <div 
          className="add-restroom-confirmation-dialog"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            zIndex: 1600,
            maxWidth: '300px',
            borderTop: '4px solid #5752e8',
            textAlign: 'center'
          }}
        >
          <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>Move "{restroomToMove?.name}" to the center of the map?</p>
          <p style={{ marginBottom: '15px', fontSize: '12px', color: '#666' }}>Drag the map to align the restroom with the crosshair.</p>
          <div className="confirmation-actions">
            <button 
              className="btn btn-primary"
              style={{ backgroundColor: '#4a3728', color: 'white' }}
              onClick={handleConfirmMoveRestroom}
            >
              ✓ Confirm Move
            </button>
            <button className="btn btn-secondary" onClick={handleCancelMoveRestroom}>✗ Cancel</button>
          </div>
        </div>
      )}

      {showAddModal && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <AddRestroomModal
            userLocation={userLocation}
            selectedMapLocation={selectedMapLocation}
            adminMode={currentUser?.isAdmin}
            areyouatPlaces={addModalAreYouAtPlaces} // Pass fetched places
            nearbyVerifiedRestrooms={addModalNearbyRestrooms} // Pass fetched restrooms
            checkingNearbyRestrooms={addModalCheckingNearby} // Pass loading state
            onEnableMapSelection={() => {
              setShowAddModal(false);
              setIsAddingRestroomMode(false);
              setMapSelectionMode(true);
              const centerLoc = selectedMapLocation || userLocation;
              if (centerLoc) {
                setMapCenter(centerLoc);
                setMapZoom(19);
              }
            }}
            onClose={() => {
            setShowAddModal(false);
            setMapSelectionMode(false);
            setSelectedMapLocation(null);
            setIsAddingRestroomMode(false);
            setAddModalNearbyRestrooms([]); // Clear state on close
            setAddModalAreYouAtPlaces([]); // Clear state on close
          }}
          onSelectExistingRestroom={handleSelectExistingRestroom}
          onRestroomAdded={handleRestroomAdded}
          onGamification={setGamificationPayload}
        />
        </Suspense>
      )}
      
      {showDetailsModal && selectedRestroom && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <RestroomDetailsModal 
          key={selectedRestroom.id}
          restroom={selectedRestroom}
          userLocation={userLocation}
          initialShowReview={showReviewOnOpen}
          onClose={() => {
            setShowDetailsModal(false);
            setMapSelectionMode(false);
            setSelectedMapLocation(null);

            // Pop the poop confetti over the map when the details modal closes
            // right after a review was added, alongside the gamification popup.
            if (reviewJustAddedRef.current) {
              reviewJustAddedRef.current = false;
              if (selectedRestroom) setCelebrationRestroom(selectedRestroom);
            }

            const preservedMapCenter = selectedRestroom?.latitude && selectedRestroom?.longitude
              ? { lat: selectedRestroom.latitude, lng: selectedRestroom.longitude }
              : null;
            const preservedMapState = preservedMapCenter
              ? { preservedMapCenter, preservedMapZoom: 17 }
              : {};

            if (preservedMapCenter) {
              setMapCenter(preservedMapCenter);
              setMapZoom(17);
            }

            // Clear sharedRestroomId from URL if it was used to open this modal
            if (sharedRestroomId) {
              navigate(location.pathname.replace(`/r/${sharedRestroomId}`, ''), { replace: true, state: preservedMapState });
            }
            // Clear selectedRestroomId from location.state if it was used
            else if (location.state?.selectedRestroomId) {
              navigate(location.pathname, { replace: true, state: preservedMapState });
            }
          }}
          onAddNewLocation={(location) => {
            setSelectedMapLocation(location);
            setShowDetailsModal(false);
            setShowAddModal(true);
          }}
          onMoveRestroom={handleMoveRestroom}
          onStartMerge={handleStartMerge}
          onRequireTOS={() => setShowTOSModal(true)}
          onReviewAdded={handleReviewAdded}
          onGamification={setGamificationPayload}
        />
        </Suspense>
      )}
      
      {showSearchModal && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <SearchModal
          userLocation={userLocation}
          onClose={() => setShowSearchModal(false)}
          onRestroomSelect={handleRestroomClick}
          onViewResultsOnMap={(filtered) => {
            setSearchFilteredRestrooms(filtered);
            setShowSearchModal(false);
            toast.success(`Showing ${filtered.length} results on map`);
          }}
        />
        </Suspense>
      )}
      
      {showUserMenu && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <UserMenu 
          user={currentUser}
          onClose={() => setShowUserMenu(false)} 
          onLogout={signOut}
          onRestroomClick={handleRestroomClick}
          onShowContactUs={() => { setShowContactUsModal(true); setShowUserMenu(false); }} // Pass the new prop
        />
        </Suspense>
      )}

      {showInbox && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <InboxPopup onClose={() => setShowInbox(false)} onNotificationsRead={() => setUnreadCount(0)} />
        </Suspense>
      )}
      
      {showOnboarding && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <OnboardingTutorial onComplete={() => {
            setShowOnboarding(false);
            if (currentUser?.uid) {
              localStorage.setItem(`crapperMapper_tutorialCompleted_${currentUser.uid}`, 'true');
            }
          }} />
        </Suspense>
      )}

      {showTOSModal && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <TermsOfServiceModal onAccept={handleTOSAccept} onDecline={() => { setShowTOSModal(false); toast.error('ToS required'); }} />
        </Suspense>
      )}

      {showContactUsModal && (
        <Suspense fallback={<div className="modal-loading">Loading...</div>}>
          <ContactUsModal onClose={() => setShowContactUsModal(false)} />
        </Suspense>
      )}

      <GamificationCompletionPopup
        payload={gamificationPayload}
        onClose={() => setGamificationPayload(null)}
      />
    </div>
  );
}

export default MapScreen;
